// AssoBenevoles.jsx — Planning des missions bénévoles (bureau) : calendrier + liste + CRUD + relance.
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, MapPin, Users, Trash2, Pencil, Send, Check,
  ChevronLeft, ChevronRight, CalendarDays, List, HeartHandshake,
} from 'lucide-react';

const WD = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function AssoBenevoles() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar');
  const [cursor, setCursor] = useState(() => new Date());
  const [didInit, setDidInit] = useState(false);
  const [editing, setEditing] = useState(null); // mission | 'new' | {date}

  const load = async () => {
    try { const { data } = await api.get('/missions'); setMissions(data); }
    catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!didInit && missions.length) {
      const dates = missions.map((m) => m.date).filter(Boolean).sort();
      if (dates[0]) setCursor(new Date(`${dates[0]}T00:00:00`));
      setDidInit(true);
    }
  }, [missions, didInit]);

  const byDate = useMemo(() => {
    const m = {};
    missions.forEach((x) => { (m[x.date] = m[x.date] || []).push(x); });
    return m;
  }, [missions]);

  const remove = async (m) => {
    if (!window.confirm(`Supprimer la mission « ${m.titre} » ?`)) return;
    try { await api.delete(`/missions/${m.id}`); toast.success('Mission supprimée'); load(); }
    catch { toast.error('Suppression impossible'); }
  };
  const relancer = async (m) => {
    try {
      const { data } = await api.post(`/missions/${m.id}/relancer`);
      toast.success(data.mocked && !data.sent ? `Relance simulée (Brevo non configuré) — ${data.total} bénévole·s` : `Relance envoyée à ${data.total} bénévole·s`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Relance impossible'); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="asso-benevoles">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>Engagement</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
            Planning <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>bénévoles.</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg border-2 border-[#E2E8F0] overflow-hidden">
            <button onClick={() => setView('calendar')} className="px-3 py-2 text-sm font-bold flex items-center gap-1.5" style={view === 'calendar' ? { background: 'var(--theme-primary)', color: '#fff' } : { color: '#475569' }} data-testid="view-calendar"><CalendarDays className="w-4 h-4" /> Calendrier</button>
            <button onClick={() => setView('list')} className="px-3 py-2 text-sm font-bold flex items-center gap-1.5" style={view === 'list' ? { background: 'var(--theme-primary)', color: '#fff' } : { color: '#475569' }} data-testid="view-list"><List className="w-4 h-4" /> Liste</button>
          </div>
          <button onClick={() => setEditing('new')} className="btn-primary" data-testid="new-mission-btn"><Plus className="w-4 h-4" /> Nouvelle mission</button>
        </div>
      </div>

      {loading ? (
        <div className="h-96 rounded-2xl bg-white border border-[#E2E8F0] animate-pulse" />
      ) : view === 'calendar' ? (
        <CalendarView cursor={cursor} setCursor={setCursor} byDate={byDate} onOpen={setEditing} />
      ) : (
        <ListView missions={missions} onEdit={setEditing} onDelete={remove} onRelancer={relancer} />
      )}

      {editing && (
        <MissionModal
          mission={editing === 'new' || editing?.dateOnly ? null : editing}
          initialDate={editing?.dateOnly || ''}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={remove}
          onRelancer={relancer}
        />
      )}
    </div>
  );
}

function CalendarView({ cursor, setCursor, byDate, onOpen }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startDay = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const iso = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white border-2 border-[#E2E8F0] rounded-2xl p-4 sm:p-6" data-testid="mission-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="btn-secondary !px-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-display font-black text-xl text-[#0F172A]">{MONTHS[month]} {year}</div>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="btn-secondary !px-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {WD.map((d) => <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] pb-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const dateStr = iso(d);
          const items = byDate[dateStr] || [];
          const isToday = dateStr === todayIso;
          return (
            <div key={dateStr} className="min-h-[84px] rounded-lg border p-1.5 flex flex-col gap-1" style={{ borderColor: isToday ? 'var(--theme-primary)' : '#F1F5F9', background: isToday ? 'var(--theme-primary-soft)' : '#fff' }} data-testid={`cal-day-${dateStr}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#0F172A]">{d}</span>
                <button onClick={() => onOpen({ dateOnly: dateStr })} className="text-[#CBD5E1] hover:text-[var(--theme-primary)]" data-testid={`cal-add-${dateStr}`}><Plus className="w-3.5 h-3.5" /></button>
              </div>
              {items.map((m) => (
                <button key={m.id} onClick={() => onOpen(m)} className="text-left text-[10px] font-semibold rounded px-1.5 py-1 truncate text-white" style={{ background: 'var(--theme-primary)' }} title={m.titre} data-testid={`cal-mission-${m.id}`}>
                  {m.titre} · {m.nb_inscrits}/{m.besoins}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ missions, onEdit, onDelete, onRelancer }) {
  if (missions.length === 0) {
    return (
      <div className="bg-white border-2 border-dashed border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="missions-empty">
        <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-3 text-[#94A3B8]"><HeartHandshake className="w-7 h-7" /></div>
        <h3 className="font-display font-black text-[#0F172A]">Aucune mission</h3>
        <p className="text-sm text-[#475569] mt-1">Créez votre première mission bénévole.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="missions-list">
      {missions.map((m) => (
        <div key={m.id} className="bg-white border-2 border-[#E2E8F0] rounded-2xl p-5" data-testid={`mission-item-${m.id}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-display font-black text-lg text-[#0F172A]">{m.titre}</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white shrink-0" style={{ background: m.places_restantes > 0 ? '#F59E0B' : '#10B981' }}>{m.places_restantes > 0 ? `${m.places_restantes} place·s` : 'Complet'}</span>
          </div>
          {m.description && <p className="text-sm text-[#475569] mb-3">{m.description}</p>}
          <div className="space-y-1.5 text-sm text-[#475569] mb-3">
            {m.date && <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {m.date}</div>}
            {m.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {m.lieu}</div>}
            <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {m.benevoles.length ? m.benevoles.map((b) => b.prenom + ' ' + b.nom).join(', ') : 'Aucun inscrit'} ({m.nb_inscrits}/{m.besoins})</div>
          </div>
          <div className="flex gap-2 pt-3 border-t border-[#F1F5F9]">
            <button onClick={() => onRelancer(m)} disabled={m.nb_inscrits === 0} className="btn-secondary !text-xs flex-1 disabled:opacity-40" data-testid={`relancer-${m.id}`}><Send className="w-3 h-3" /> Relancer</button>
            <button onClick={() => onEdit(m)} className="btn-secondary !text-xs" data-testid={`edit-mission-${m.id}`}><Pencil className="w-3 h-3" /></button>
            <button onClick={() => onDelete(m)} className="btn-secondary !text-xs !text-[#EF4444]" data-testid={`delete-mission-${m.id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MissionModal({ mission, initialDate, onClose, onSaved, onDelete, onRelancer }) {
  const isNew = !mission;
  const [form, setForm] = useState({
    titre: mission?.titre || '', description: mission?.description || '',
    date: mission?.date || initialDate || '', lieu: mission?.lieu || '',
    besoins: mission?.besoins ?? 1,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.titre.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      if (isNew) await api.post('/missions', form);
      else await api.patch(`/missions/${mission.id}`, form);
      toast.success(isNew ? 'Mission créée' : 'Mission mise à jour');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0F172A]/70 backdrop-blur-md grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="mission-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-[#F1F5F9] flex items-center justify-between">
          <h2 className="font-display text-2xl font-black text-[#0F172A]">{isNew ? 'Nouvelle mission' : 'Modifier la mission'}</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="mission-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Titre *"><input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="input-base" data-testid="mission-titre" /></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-base" rows={3} data-testid="mission-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-base" data-testid="mission-date" /></Field>
            <Field label="Bénévoles requis"><input type="number" min="0" value={form.besoins} onChange={(e) => setForm({ ...form, besoins: e.target.value })} className="input-base" data-testid="mission-besoins" /></Field>
          </div>
          <Field label="Lieu"><input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} className="input-base" data-testid="mission-lieu" /></Field>
          {!isNew && mission.benevoles?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-1.5">Inscrits ({mission.benevoles.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {mission.benevoles.map((b) => <span key={b.id} className="text-xs font-semibold bg-[#F1F5F9] rounded-full px-3 py-1.5">{b.prenom} {b.nom}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#F1F5F9] flex justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            {!isNew && <button onClick={() => { onClose(); onDelete(mission); }} className="btn-secondary !text-[#EF4444]" data-testid="mission-delete"><Trash2 className="w-4 h-4" /></button>}
            {!isNew && mission.nb_inscrits > 0 && <button onClick={() => onRelancer(mission)} className="btn-secondary" data-testid="mission-relancer"><Send className="w-4 h-4" /> Relancer</button>}
          </div>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="mission-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {isNew ? 'Créer' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
